import { useEffect, useRef, useState } from 'react';
import { LogOut, ShieldAlert, User2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { useLogout, useStopPermissionSimulation } from '@/features/auth/useAuth';
import { NotificationBell } from '@/features/notifications/NotificationBell';
import { apiClient } from '@/lib/api-client';
import { useCurrentUser } from '@/lib/auth-context';
import { canAccessPath, homeRouteFor } from '@/lib/role-access';
import { BatchSelector } from './BatchSelector';
// FacilitySelector was a stub showing "無資料" with no real data — removed
// from the TopNav. Single-facility ops auto-pick the only one; multi-facility
// support can re-introduce a wired selector later.
import { LangSwitcher } from './LangSwitcher';
import { ThemeToggle } from './ThemeToggle';
import { FieldContrastToggle } from './FieldContrastToggle';

/**
 * Poll `/api/health` every 30 s to surface backend connectivity.
 * A red dot indicates the backend is unreachable.
 */
const useHealthStatus = () => {
  const { data, isError } = useQuery({
    queryKey: ['health'],
    queryFn: async () => {
      const res = await apiClient.get('/health');
      return res.data;
    },
    refetchInterval: 30_000,
    retry: 1,
  });
  return { online: !!data && !isError, offline: isError };
};

export const TopNav = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { offline } = useHealthStatus();
  const user = useCurrentUser();
  const logout = useLogout();
  const stopSimulation = useStopPermissionSimulation();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  // The BatchSelector queries /api/monthly-batches/selector-options and mounts CreateBatchDialog.
  // For roles that have no batch access (supplier/platform), hide it — leaving
  // it mounted causes a render loop and noisy 403s.
  const showBatchSelector = canAccessPath(user, '/batches');
  const userLabel = user?.displayName || user?.email || user?.username || '';
  const isSimulating = !!user?.simulatedBy;

  useEffect(() => {
    if (!userMenuOpen) {
      return;
    }

    const closeWhenOutside = (event: MouseEvent) => {
      if (!userMenuRef.current?.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', closeWhenOutside);
    return () => document.removeEventListener('mousedown', closeWhenOutside);
  }, [userMenuOpen]);

  const handleLogout = async () => {
    setUserMenuOpen(false);
    await logout.mutateAsync();
    navigate('/login', { replace: true });
  };

  const handleStopSimulation = async () => {
    setUserMenuOpen(false);
    const restoredUser = await stopSimulation.mutateAsync();
    navigate(homeRouteFor(restoredUser), { replace: true });
  };

  return (
    <header
      className="flex min-h-12 shrink-0 items-center justify-between gap-2 border-b border-efs-border bg-efs-bg-panel px-2 py-1 sm:h-12 sm:px-4 sm:py-0"
      role="banner"
    >
      <div className="min-w-0 flex-1">{showBatchSelector ? <BatchSelector /> : null}</div>
      <div className="flex shrink-0 items-center gap-1">
        {offline ? (
          <span className="flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium bg-red-100 text-red-900 dark:bg-red-900/40 dark:text-red-100">
            <span className="inline-block h-2 w-2 rounded-full bg-red-500" />
            {t('efs.common.topnav.offline')}
          </span>
        ) : null}
        <NotificationBell />
        <LangSwitcher />
        <ThemeToggle />
        <FieldContrastToggle />
        {isSimulating ? (
          <span className="hidden items-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 sm:flex">
            <ShieldAlert className="h-3.5 w-3.5" />
            {t('efs.common.topnav.simulating')}
          </span>
        ) : null}
        <div className="relative" ref={userMenuRef}>
          <Button
            variant="ghost"
            size="icon"
            aria-expanded={userMenuOpen}
            aria-haspopup="menu"
            aria-label={t('efs.common.topnav.user_menu')}
            title={t('efs.common.topnav.user_menu')}
            onClick={() => setUserMenuOpen((open) => !open)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                setUserMenuOpen(false);
              }
            }}
          >
            {isSimulating ? <ShieldAlert className="h-4 w-4" /> : <User2 className="h-4 w-4" />}
          </Button>
          {userMenuOpen ? (
            <div
              role="menu"
              className="absolute right-0 top-full z-50 mt-1 w-56 overflow-hidden rounded-md border border-efs-border bg-efs-bg-panel p-1 text-efs-text shadow-md"
            >
              {userLabel ? (
                <>
                  <div className="truncate px-2 py-1.5 text-sm font-semibold">{userLabel}</div>
                  {isSimulating && user.simulatedBy ? (
                    <div className="px-2 pb-1 text-xs text-amber-900">
                      {t('efs.common.topnav.simulating_from', {
                        name: user.simulatedBy.displayName || user.simulatedBy.email,
                      })}
                    </div>
                  ) : null}
                  <div className="-mx-1 my-1 h-px bg-muted" />
                </>
              ) : null}
              {isSimulating ? (
                <button
                  type="button"
                  role="menuitem"
                  disabled={stopSimulation.isPending}
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm text-amber-800 outline-none transition-colors hover:bg-amber-50 focus:bg-amber-50 disabled:pointer-events-none disabled:opacity-50"
                  onClick={() => {
                    void handleStopSimulation();
                  }}
                >
                  <ShieldAlert className="h-4 w-4" />
                  {t('efs.common.topnav.stop_simulation')}
                </button>
              ) : null}
              <button
                type="button"
                role="menuitem"
                disabled={logout.isPending}
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
                onClick={() => {
                  void handleLogout();
                }}
              >
                <LogOut className="h-4 w-4" />
                {t('efs.common.topnav.logout')}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
};
